import {
  Column,
  ColumnDef,
  RowData,
  flexRender,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn,
  SortingState,
  getSortedRowModel,
  getFilteredRowModel,
  FilterFn,
  getFacetedRowModel,
  getFacetedUniqueValues,
} from "@tanstack/react-table";
import clsx from "clsx";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

import { useStorage } from "@smart/itops-hooks-dom";
import { entriesOf } from "@smart/itops-utils-basic";

import { BulkActionsButton } from "./bulk-actions-button";
import {
  getCellWrapperClassNames,
  getDataCellClassNames,
  getDataRowClassNames,
  getHeaderCellClassNames,
  getHeaderCellContainerClassNames,
  getHeaderCellWrapperClassNames,
  tableStyles,
} from "./class-names";
import {
  CellSkeleton,
  createSelectionColumn,
  SELECTION_COLUMN_ID,
} from "./table-part";
import { Toolbar } from "./toolbar";
import { Badge } from "../badge";
import { Icon } from "../icon";
import { ProgressBar } from "../progress-bar";
import { TimePeriod } from "../time-filter";
import { Tooltip } from "../tooltip";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    action?: boolean;
    filter?: {
      type?: "select" | "dateRange" | "radio" | "textInput";
      order?: number;
      title?: string;
      name: string;
      placeholder?: string;
      getItemLabel?: (value: TValue) => string;
      fixedOptions?: { value: string; label: string }[];
      defaultOption?: string;
      dateRangeExcludedOptions?: TimePeriod[];
      onFilterValueChanged?: (value: TValue) => void;
      unrestricted?: boolean;
    };
    showRowCount?: boolean;
    className?: string;
  }
}

type TableState<S> = { state: S; onChange: OnChangeFn<S> };

export type TableProps<I extends {}> = {
  id?: string;
  data: I[];
  columns: ColumnDef<I, any>[];
  updating?: boolean;
  loadingRows?: number;
  getRowId?: (row: I) => string;
  select: TableState<RowSelectionState> | undefined;
  sort: TableState<SortingState> | undefined;
  globalFilter: (TableState<string> & { fn: FilterFn<I> }) | undefined;
  empty?: ReactNode;
  applyColumnSize?: boolean;
  disabled?: boolean;
  standalone?: boolean;
  multiSelect?: boolean;
  batchActions?: {
    text: string;
    onClick: (selected: I[]) => Promise<void> | void;
    className?: string;
  }[];
  enableColumnFilters?: boolean;
  isRowClickable?: (row: I) => boolean;
  onRowClick?: (row: I) => void;
  className?: string;
  fixedHeadings?: boolean;
};

export const Table = <I extends {}>({
  id,
  data,
  columns,
  updating,
  loadingRows,
  getRowId,
  select,
  sort,
  globalFilter,
  empty,
  applyColumnSize,
  disabled,
  standalone,
  multiSelect,
  batchActions,
  enableColumnFilters,
  isRowClickable = () => true,
  onRowClick,
  className,
  fixedHeadings,
}: TableProps<I>) => {
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const tableId = id ?? columns.map((c) => c.id || c.header).join("-");
  const [columnFilters, setColumnFilters] = useStorage({
    defaultValue: [],
    key: `${tableId}-column-filters`,
    schema: z.array(z.any()),
    storage: "session",
  });

  const selectionColumn = createSelectionColumn<I>();
  const tableColumns = multiSelect ? [selectionColumn, ...columns] : columns;

  const table = useReactTable({
    state: {
      rowSelection,
      sorting: sort?.state,
      globalFilter: globalFilter?.state,
      columnFilters,
    },
    data,
    columns: tableColumns,
    enableRowSelection: !!select,
    onRowSelectionChange: (rowSelectionState) => {
      if (multiSelect) setRowSelection(rowSelectionState);
    },
    enableSorting: !!sort,
    onSortingChange: sort?.onChange,
    globalFilterFn: globalFilter?.fn,
    onGlobalFilterChange: select?.onChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnFiltersChange: setColumnFilters,
    getRowId,
  });

  const sortingIcon = (isSorted: "asc" | "desc" | "none") => {
    if (isSorted === "none") return <span className="w-4" />;
    return (
      <Icon
        className="size-4"
        name={isSorted === "asc" ? "regularArrowUp" : "regularArrowDown"}
      />
    );
  };

  const noop = () => {};

  const isSelectionColumn = (column: Column<I>) =>
    column.id === SELECTION_COLUMN_ID;

  const selectedRows = useMemo(
    () =>
      entriesOf(rowSelection)
        .map(([rowId]) =>
          data.find((d, i) => (getRowId ? getRowId(d) : i) === rowId),
        )
        .filter(Boolean) as I[],
    [rowSelection, data],
  );

  useEffect(() => {
    if (multiSelect) select?.onChange(rowSelection);
  }, [rowSelection]);

  const isEmpty = !loadingRows && !!empty && table.getRowCount() === 0;

  return (
    <div className="flex flex-col h-full">
      <Toolbar
        table={table}
        clearRowSelection={() => setRowSelection({})}
        enableColumnFilters={enableColumnFilters}
        leftPanel={
          multiSelect &&
          batchActions?.length && (
            <BulkActionsButton
              selectedRows={selectedRows}
              batchActions={batchActions}
              loading={batchActionLoading}
              setLoading={setBatchActionLoading}
            />
          )
        }
        loading={!!loadingRows}
      />
      <div
        className={twMerge(
          clsx(
            "min-h-0 flex-1 overflow-auto w-full border-neutral-100 rounded-b",
            isEmpty ? "h-full [&>table]:h-full" : "border-y",
            standalone && "border",
            standalone && "rounded",
            className,
          ),
        )}
      >
        <table className={tableStyles}>
          <thead
            className={clsx(fixedHeadings && "sticky top-0 bg-white z-10")}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    id={header.column.id}
                    title={
                      isSelectionColumn(header.column)
                        ? undefined
                        : header.column.columnDef.header?.toString()
                    }
                    onClick={
                      disabled ? noop : header.column.getToggleSortingHandler()
                    }
                    className={twMerge(
                      getHeaderCellClassNames({
                        sortable: header.column.getCanSort(),
                        disabled,
                        isSelectionColumn: isSelectionColumn(header.column),
                      }),
                    )}
                    aria-sort={
                      (
                        {
                          asc: "ascending",
                          desc: "descending",
                          none: undefined,
                        } as const
                      )[header.column.getIsSorted() || "none"]
                    }
                  >
                    <span
                      className={getHeaderCellContainerClassNames({
                        isSelectionColumn: isSelectionColumn(header.column),
                      })}
                    >
                      <span className={getHeaderCellWrapperClassNames()}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </span>
                      {header.column.columnDef.meta?.showRowCount && (
                        <Badge
                          dataTestId="row-count"
                          theme="emphasis"
                          isRound
                          label={table.getRowCount()}
                        />
                      )}
                      {header.column.getCanSort() &&
                        sortingIcon(header.column.getIsSorted() || "none")}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
            {updating && (
              <tr className="relative">
                <td className="p-0 border-b-0">
                  <ProgressBar overlay className="h-[0.2rem]" />
                </td>
              </tr>
            )}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                aria-selected={row.getIsSelected()}
                className={twMerge(
                  getDataRowClassNames({
                    clickable: !!onRowClick && isRowClickable(row.original),
                    selected: row.getIsSelected(),
                  }),
                )}
              >
                {row.getAllCells().map((cell, index) => (
                  <td
                    key={cell.id}
                    aria-describedby={cell.column.id}
                    className={twMerge(
                      getDataCellClassNames({
                        action: cell.column.columnDef.meta?.action,
                        selectable: cell.row.getCanSelect(),
                        minWidth: applyColumnSize
                          ? cell.column.getSize()
                          : undefined,
                        width: applyColumnSize
                          ? cell.column.getSize()
                          : undefined,
                        className: cell.column.columnDef.meta?.className,
                        disabled,
                      }),
                    )}
                    onClick={
                      cell.column.columnDef.meta?.action ||
                      !isRowClickable(row.original)
                        ? undefined
                        : () => onRowClick?.(row.original)
                    }
                  >
                    <div
                      className={getCellWrapperClassNames({
                        selected:
                          row.getIsSelected() && index === 0 && !multiSelect,
                        isSelectionColumn: isSelectionColumn(cell.column),
                      })}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {loadingRows &&
              Array.from({ length: loadingRows }, (_, i) => (
                <tr key={`loading-${i}`}>
                  {table.getAllLeafColumns().map((column) => (
                    <td
                      key={column.id}
                      className={getDataCellClassNames({
                        maxWidth: column.getSize(),
                        minWidth: column.getSize(),
                      })}
                    >
                      <div className={getCellWrapperClassNames({})}>
                        <div className="h-6">
                          <CellSkeleton />
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            {isEmpty && (
              <tr>
                <td colSpan={100} className={getDataCellClassNames({})}>
                  <span className="flex flex-col flex-nowrap items-center justify-center text-center px-6 py-4">
                    {empty}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const CellWrapper = ({
  label,
  size = "standard",
  maxWidth,
  className,
  hideTooltip,
}: {
  label: string;
  size?: "small" | "standard" | "medium" | "large";
  // Max width needs to be the complete class name string, e.g. "max-w-[20rem]"
  maxWidth?: string;
  className?: string;
  hideTooltip?: boolean;
}) => {
  const sizeMap: Record<typeof size, string> = {
    small: "max-w-[8rem]",
    medium: "max-w-[20rem]",
    standard: "max-w-[16rem]",
    large: "max-w-[24rem]",
  };

  return (
    <Tooltip
      text={hideTooltip ? undefined : label}
      placement="top-end"
      offsetBy={16}
    >
      <div
        className={clsx(
          "overflow-hidden text-ellipsis whitespace-nowrap",
          maxWidth,
          !maxWidth && size && sizeMap[size],
          className,
        )}
      >
        {label}
      </div>
    </Tooltip>
  );
};
